import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { labAPI } from '../../utils/api';

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const TESTS = [
    'CBC (Complete Blood Count)',
    'FBS (Fasting Blood Sugar)',
    'RBS (Random Blood Sugar)',
    'HbA1c (3-Month Average Sugar)',
    'LFTs (Liver Function Tests)',
    'RFTs / KFTs (Kidney Function Tests)',
    'Serum Creatinine',
    'Serum Uric Acid',
    'Lipid Profile (Cholesterol & Triglycerides)',
    'Serum Electrolytes (Sodium, Potassium, Chloride)',
    'TSH (Thyroid Stimulating Hormone)',
    'Free T3 & Free T4',
    'Vitamin D3',
    'Vitamin B12',
    'HBsAg (Hepatitis B Screening)',
    'Anti-HCV (Hepatitis C Screening)',
    'HIV 1 & 2 Screening',
    'Hepatitis B & C PCR (Viral Load)',
    'TB GeneXpert / PCR',
    'COVID-19 RT-PCR',
    'PT / INR (Prothrombin Time / Clotting)',
    'APTT (Activated Partial Thromboplastin Time)',
    'D-Dimer',
    'ESR (Erythrocyte Sedimentation Rate)',
    'CRP (C-Reactive Protein)',
    'hs-CRP (High-Sensitivity CRP)',
    'Typhidot / Widal (Typhoid Test)',
    'Malarial Parasite (MP / ICT)',
    'Dengue NS1 / IgG / IgM',
    'RA Factor (Rheumatoid Arthritis)',
    'Anti-CCP',
    'ANA (Antinuclear Antibody)',
    'Troponin-I (Heart Attack Marker)',
    'CK-MB (Cardiac Marker)',
    'PSA (Prostate Cancer Marker)',
    'CEA (Cancer Marker)',
    'CA-125 (Ovarian Cancer Marker)',
    'CA 19-9 (Pancreatic Cancer Marker)',
    'AFP (Alpha-Fetoprotein)',
    'Beta-hCG (Pregnancy Test)',
    'FSH & LH (Fertility Hormones)',
    'Prolactin',
    'Testosterone',
    'AMH (Anti-Müllerian Hormone)',
    'Ferritin (Iron Stores)',
    'TIBC (Total Iron Binding Capacity)',
    'Hemoglobin Electrophoresis (Thalassemia Test)',
    'Urine R/E (Routine Examination)',
    'Urine Culture & Sensitivity',
    'Stool R/E (Routine Examination)',
    'Stool Culture',
    'Blood Culture & Sensitivity',
    'Sputum AFB (TB Bacteria Test)',
    'Semen Analysis',
    'Biopsy / Histopathology (Tissue Cancer Test)',
    'Pap Smear (Cytology Test)',
    'Blood Grouping & RH Factor',
];

const MACHINES = [
    'Mindray',
    'Sysmex',
    'Abbott',
    'Roche',
    'Randox',
    'Bio-Rad',
    'Cepheid',
    'Stago',
    'Siemens',
    'Beckman Coulter',
    'bioMérieux',
    'BD (Becton Dickinson)',
    'Snibe',
    'Thermo Fisher',
    'Olympus',
];

const BLANK = {
    machine_name: '', reagent_lot: '', qc_level: 'Normal',
    test_name: '', expected_value: '', obtained_value: '', comments: ''
};

export default function LabQCLog() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const pkToday = () => new Date(Date.now() + 5*60*60*1000).toISOString().split('T')[0];
    const [date, setDate] = useState(pkToday());
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [testSearch, setTestSearch] = useState('');
    const [showTestList, setShowTestList] = useState(false);
    const [machineSearch, setMachineSearch] = useState('');
    const [showMachineList, setShowMachineList] = useState(false);
    const filteredTests = TESTS.filter(t => t.toLowerCase().includes(testSearch.toLowerCase()));
    const filteredMachines = MACHINES.filter(m => m.toLowerCase().includes(machineSearch.toLowerCase()));

    const load = async () => {
        setLoading(true);
        try {
            const res = await labAPI.getQCLog(date);
            setEntries(res.data);
        } catch { toast.error('Failed to load QC log'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [date]);

    const save = async () => {
        if (!form.test_name || !form.obtained_value) return toast.error('Test name and obtained value required');
        setSaving(true);
        try {
            await labAPI.addQCEntry(form);
            toast.success('QC entry added');
            setShowModal(false);
            setForm(BLANK);
            setTestSearch('');
            setMachineSearch('');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-PK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🧫 QC Log</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Quality Control entries</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
                    <button onClick={() => setDate(pkToday())}
                        style={{ padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Today</button>
                    <button onClick={() => setShowModal(true)}
                        style={{ padding: '8px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                        + Add QC Entry
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            ) : (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Test Name','Machine','Reagent Lot','QC Level','Expected','Obtained','Deviation%','Status','Comments','By','Time'].map(h => (
                                    <th key={h} style={{ padding: '9px 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textAlign: 'left', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && (
                                <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No QC entries for this date</td></tr>
                            )}
                            {entries.map(e => (
                                <tr key={e.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700 }}>{e.test_name}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#374151' }}>{e.machine_name || '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#374151' }}>{e.reagent_lot || '—'}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                            background: e.qc_level === 'Low' ? '#eff6ff' : e.qc_level === 'High' ? '#fef9c3' : '#f0fdf4',
                                            color: e.qc_level === 'Low' ? '#1d4ed8' : e.qc_level === 'High' ? '#854d0e' : '#166534' }}>
                                            {e.qc_level}
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{e.expected_value ?? '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{e.obtained_value ?? '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: e.deviation_percent > 10 ? '#dc2626' : '#374151' }}>
                                        {e.deviation_percent != null ? `${e.deviation_percent}%` : '—'}
                                    </td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
                                            background: e.status === 'Pass' ? '#dcfce7' : '#fef2f2',
                                            color: e.status === 'Pass' ? '#16a34a' : '#dc2626' }}>
                                            {e.status === 'Pass' ? '✓ Pass' : '✕ Fail'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b', maxWidth: 150 }}>{e.comments || '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{e.performed_by_name || '—'}</td>
                                    <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{fmt(e.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add QC Modal */}
            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: 'white', borderRadius: 14, padding: 28, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>Add QC Entry</h3>
                            <button onClick={() => { setShowModal(false); setForm(BLANK); setTestSearch(''); setMachineSearch(''); }}
                                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {/* Test Name Dropdown */}
                            <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Test Name *</label>
                                <input style={inputStyle}
                                    value={form.test_name || testSearch}
                                    placeholder="Search test..."
                                    onChange={e => { setTestSearch(e.target.value); setForm(f => ({ ...f, test_name: '' })); setShowTestList(true); }}
                                    onFocus={() => setShowTestList(true)}
                                    onBlur={() => setTimeout(() => setShowTestList(false), 150)}
                                />
                                {showTestList && filteredTests.length > 0 && (
                                    <div style={{ position: 'absolute', zIndex: 200, background: 'white', border: '1px solid #d1d5db', borderRadius: 7, width: '100%', maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                        {filteredTests.map(t => (
                                            <div key={t} onMouseDown={() => { setForm(f => ({ ...f, test_name: t })); setTestSearch(''); setShowTestList(false); }}
                                                style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}
                                                onMouseEnter={e => e.target.style.background = '#f5f3ff'}
                                                onMouseLeave={e => e.target.style.background = 'white'}>
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Machine Name Dropdown */}
                            <div style={{ position: 'relative' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Machine Name</label>
                                <input style={inputStyle}
                                    value={form.machine_name || machineSearch}
                                    placeholder="Search machine..."
                                    onChange={e => { setMachineSearch(e.target.value); setForm(f => ({ ...f, machine_name: '' })); setShowMachineList(true); }}
                                    onFocus={() => setShowMachineList(true)}
                                    onBlur={() => setTimeout(() => setShowMachineList(false), 150)}
                                />
                                {showMachineList && filteredMachines.length > 0 && (
                                    <div style={{ position: 'absolute', zIndex: 100, background: 'white', border: '1px solid #d1d5db', borderRadius: 7, minWidth: '100%', width: 'max-content', maxWidth: 380, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                        {filteredMachines.map(m => (
                                            <div key={m} onMouseDown={() => { setForm(f => ({ ...f, machine_name: m })); setMachineSearch(''); setShowMachineList(false); }}
                                                style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}
                                                onMouseEnter={e => e.target.style.background = '#f5f3ff'}
                                                onMouseLeave={e => e.target.style.background = 'white'}>
                                                {m}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Reagent Lot</label>
                                <input style={inputStyle} value={form.reagent_lot} onChange={e => setForm(f => ({ ...f, reagent_lot: e.target.value }))} placeholder="e.g. LOT-2025-007" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>QC Level</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={form.qc_level} onChange={e => setForm(f => ({ ...f, qc_level: e.target.value }))}>
                                    <option>Low</option><option>Normal</option><option>High</option>
                                </select>
                            </div>
                            <div />
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Expected Value</label>
                                <input type="number" step="0.001" style={inputStyle} value={form.expected_value} onChange={e => setForm(f => ({ ...f, expected_value: e.target.value }))} placeholder="e.g. 14.5" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Obtained Value *</label>
                                <input type="number" step="0.001" style={inputStyle} value={form.obtained_value} onChange={e => setForm(f => ({ ...f, obtained_value: e.target.value }))} placeholder="e.g. 14.8" />
                            </div>
                            {form.expected_value && form.obtained_value && (
                                <div style={{ gridColumn: 'span 2' }}>
                                    {(() => {
                                        const dev = Math.abs(((parseFloat(form.obtained_value) - parseFloat(form.expected_value)) / parseFloat(form.expected_value)) * 100);
                                        const pass = dev <= 10;
                                        return (
                                            <div style={{ padding: '10px 14px', borderRadius: 8, background: pass ? '#f0fdf4' : '#fef2f2', border: `1px solid ${pass ? '#86efac' : '#fca5a5'}` }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: pass ? '#16a34a' : '#dc2626' }}>
                                                    {pass ? '✓ PASS' : '✕ FAIL'} — Deviation: {dev.toFixed(2)}%
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Comments</label>
                                <textarea style={{ ...inputStyle, height: 60 }} value={form.comments} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} placeholder="Optional notes..." />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button onClick={() => { setShowModal(false); setForm(BLANK); setTestSearch(''); setMachineSearch(''); }}
                                style={{ padding: '9px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            <button onClick={save} disabled={saving}
                                style={{ padding: '9px 24px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                                {saving ? 'Saving...' : 'Save Entry'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
